/** Formatos y etiquetas compartidos por las pantallas del dashboard comercial. */

export const euro = new Intl.NumberFormat("es-ES", {
  style: "currency",
  currency: "EUR",
  maximumFractionDigits: 0,
});

const percentFormat = new Intl.NumberFormat("es-ES", {
  style: "percent",
  maximumFractionDigits: 0,
});

/** "—" cuando no hay dato: un 0 % o 0 días inventado se leería como un resultado. */
export const percent = (value: number | null | undefined): string =>
  value === null || value === undefined ? "—" : percentFormat.format(value);

export const days = (value: number | null | undefined): string =>
  value === null || value === undefined
    ? "—"
    : `${Math.round(value)} ${Math.round(value) === 1 ? "día" : "días"}`;

/**
 * Las etapas del pipeline comercial, iguales a las de Twenty. Una etapa que no esté aquí
 * (alguien la añadió en Twenty) se pinta con su código tal cual.
 */
export const PURSUIT_STAGE_LABEL: Record<string, string> = {
  TARGET: "Target",
  PROSPECT: "Prospect",
  LEAD: "Lead",
  FUEGO_LENTO: "Fuego lento",
  REUNION: "Reunión",
  BRIEF: "Brief",
  PROPUESTA: "Propuesta",
  NEGOCIACION: "Negociación",
  CLIENTE_ALWAYS_ON: "Cliente Always-On",
  CLIENTE_EVENTUAL: "Cliente Eventual",
  CLIENTE_ESTACIONAL: "Cliente Estacional",
  CLIENTE_TEST: "Cliente Test",
  PAUSADO: "Pausado",
  EXCLIENTE: "Excliente",
  RECHAZADA: "Rechazada",
};

export const stageLabel = (stage: string): string =>
  PURSUIT_STAGE_LABEL[stage] ?? stage;

/** Columnas con color en la cabecera, para leer el tablero de un vistazo. */
export const STAGE_TONE: Record<string, "success" | "error" | undefined> = {
  CLIENTE_ALWAYS_ON: "success",
  CLIENTE_EVENTUAL: "success",
  CLIENTE_ESTACIONAL: "success",
  CLIENTE_TEST: "success",
  RECHAZADA: "error",
};

/** "hace 3 min", "hace 2 h", "hace 4 días". */
export const timeAgo = (iso: string, now: Date = new Date()): string => {
  const minutes = Math.max(
    0,
    Math.floor((now.getTime() - new Date(iso).getTime()) / 60000)
  );
  if (minutes < 1) return "hace un momento";
  if (minutes < 60) return `hace ${minutes} min`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `hace ${hours} h`;
  const daysAgo = Math.floor(hours / 24);
  return `hace ${daysAgo} ${daysAgo === 1 ? "día" : "días"}`;
};

/** "2026-10-20" → "20/10/2026", sin pasar por la zona horaria del navegador. */
export const shortDate = (iso: string | null): string => {
  if (!iso) return "";
  const [year, month, day] = iso.slice(0, 10).split("-");
  return `${day}/${month}/${year}`;
};

/** 1 de enero del año en curso, YYYY-MM-DD: el periodo por defecto. */
export const startOfYearIso = (now: Date = new Date()): string =>
  `${now.getFullYear()}-01-01`;

/** Fin de un periodo "hasta hoy": la fecha de hoy, YYYY-MM-DD, en hora local. */
export const todayIso = (now: Date = new Date()): string =>
  `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(
    now.getDate()
  ).padStart(2, "0")}`;

export type PeriodPreset = "YEAR" | "QUARTER" | "LAST_90" | "CUSTOM";

/** Desde y hasta de cada periodo predefinido. */
export const periodRange = (
  preset: Exclude<PeriodPreset, "CUSTOM">,
  now: Date = new Date()
): { from: string; to: string } => {
  const to = todayIso(now);
  if (preset === "YEAR") return { from: startOfYearIso(now), to };
  if (preset === "QUARTER") {
    const month = Math.floor(now.getMonth() / 3) * 3 + 1;
    return {
      from: `${now.getFullYear()}-${String(month).padStart(2, "0")}-01`,
      to,
    };
  }
  const start = new Date(now);
  start.setDate(start.getDate() - 90);
  return { from: todayIso(start), to };
};

export const PROPOSAL_STATUS_LABEL: Record<string, string> = {
  BACKLOG: "Backlog",
  EN_PROCESO: "En proceso",
  ENTREGADA: "Entregada",
  ACEPTADA: "Aceptada",
  RECHAZADA: "Rechazada",
};

export const ENGAGEMENT_LABEL: Record<string, string> = {
  ALWAYS_ON: "Always-On",
  EVENTUAL: "Eventual",
  ESTACIONAL: "Estacional",
  TEST: "Test",
};

export const BUSINESS_LINE_LABEL: Record<string, string> = {
  NB: "New Business",
  EB: "Existing Business",
};

export const HOUSE_NAME: Record<string, string> = {
  HOUSE_OF_MEDIA: "House of Media",
  HOUSE_OF_DIGITAL: "House of Digital",
  HOUSE_OF_STORIES: "House of Stories",
  HOUSE_OF_AI: "House of AI",
  SIN_HOUSE: "Sin House",
};

/** Los orígenes de Twenty (sourceType). */
export const ORIGIN_LABEL: Record<string, string> = {
  PROSPECCION: "Prospección",
  INBOUND: "Inbound",
  REFERRAL: "Referido",
  PARTNER: "Partner",
  EVENTO: "Evento",
  INSTITUCIONAL: "Institucional",
  PR: "PR",
  CLIENTE_EXISTENTE: "Cliente existente",
  CEO_DIRECCION: "CEO / Dirección",
  SIN_ORIGEN: "Sin origen",
};

/** Los motivos de pérdida de Twenty (lostReason). */
export const LOST_REASON_LABEL: Record<string, string> = {
  PRECIO: "Precio",
  PROPUESTA: "Propuesta",
  TIMING: "Timing",
  SIN_PRESUPUESTO: "Sin presupuesto",
  COMPETIDOR: "Competidor",
  DECISION_INTERNA: "Decisión interna",
  SIN_ADJUDICACION: "Sin adjudicación",
  OTRO: "Otro",
  SIN_MOTIVO: "Sin motivo indicado",
};

export const FUNNEL_STEP_LABEL: Record<string, string> = {
  SOLICITADAS: "Solicitadas",
  INICIADAS: "Iniciadas",
  ENTREGADAS: "Entregadas",
  CON_RESPUESTA: "Con respuesta",
  ACEPTADAS: "Aceptadas",
  CLIENTE: "Cliente",
};

export const DISCIPLINE_LABEL: Record<string, string> = {
  SIN_PROXIMO_PASO: "Sin próximo paso",
  SEGUIMIENTO_VENCIDO: "Seguimiento vencido",
  ENTREGADA_SIN_PROXIMO_PASO: "Entregada sin próximo paso",
  ENTREGA_VENCIDA: "Entrega interna vencida",
};

export const label = (map: Record<string, string>, key: string | null) =>
  key ? (map[key] ?? key) : "—";

/** Importe o "Sin indicar": un 0 € inventado se leería como un dato. */
export const amountText = (value: number | null | undefined): string =>
  value === null || value === undefined ? "Sin indicar" : euro.format(value);
